import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'

import { breakpoints } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'

interface HeaderContextValueTypes {
  scrollTriggerRef: React.RefObject<HTMLDivElement>
  isHeaderVisible: boolean
}

const HeaderContext = createContext<HeaderContextValueTypes | undefined>(
  undefined
)
interface HeaderContextProps {
  children: React.ReactNode
}

export const HeaderContextProvider: React.FC<HeaderContextProps> = ({
  children
}) => {
  const scrollTriggerRef = useRef<HTMLDivElement | null>(null)
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const router = useRouter()

  const [isHeaderVisible, setIsHeaderVisible] = useState(true)
  const lastScrollY = useRef(0)

  useEffect(() => {
    const handleScroll = () => {
      if (!scrollTriggerRef.current) {
        return
      }

      if (!isMobile) {
        setIsHeaderVisible(true)
        return
      }

      const threshold = 0 // prevent flickering
      const currentScrollY = scrollTriggerRef.current.scrollTop || 0
      const scrollDirection = currentScrollY > lastScrollY.current

      if (currentScrollY < 10) {
        setIsHeaderVisible(true)
      } else if (
        scrollDirection &&
        currentScrollY > lastScrollY.current + threshold
      ) {
        setIsHeaderVisible(false)
      } else if (
        !scrollDirection &&
        lastScrollY.current > currentScrollY + threshold
      ) {
        setIsHeaderVisible(true)
      }

      lastScrollY.current = currentScrollY
    }

    const currentElement = scrollTriggerRef.current
    if (currentElement) {
      currentElement.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (currentElement) {
        currentElement.removeEventListener('scroll', handleScroll)
      }
    }
  }, [isMobile, router.asPath])

  return (
    <HeaderContext.Provider value={{ scrollTriggerRef, isHeaderVisible }}>
      {children}
    </HeaderContext.Provider>
  )
}

export const useHeaderContext = () => {
  const context = useContext(HeaderContext)
  if (!context) {
    throw new Error(
      'useHeaderContext must be used within a HeaderContextProvider'
    )
  }
  return context
}
